/**
 * Bridge between the landlord web app and the Resida listing-autofill Chrome extension.
 *
 * - `detectPostCapability` decides what the Advertise section offers (BR-27): launch the handoff,
 *   install guidance, or a note that filling works on a computer in Chrome.
 * - `startPost` opens the site's create page and hands the listing to the extension (BR-26). It
 *   only preselects the listing: the landlord still presses Fill and Post (BR-26c).
 *
 * The extension is addressed through `externally_connectable` using its published ID.
 */
import type { ListingSiteDto } from './external-listing-posts.api';

export type PostCapability = 'installed' | 'installable' | 'unsupported_touch' | 'unsupported_desktop';

const PING_TIMEOUT_MS = 500;

/** The create page each site's form lives on; the extension binds to the tab opened here. */
const SITE_CREATE_URL: Record<ListingSiteDto, string> = {
  facebook_marketplace: 'https://www.facebook.com/marketplace/create/rental',
  craigslist: 'https://post.craigslist.org/',
};

interface UserAgentData {
  brands?: Array<{ brand: string }>;
  mobile?: boolean;
  platform?: string;
}

interface ExtensionRuntime {
  sendMessage?: (extensionId: string, message: unknown, callback: (response: unknown) => void) => void;
  lastError?: unknown;
}

let warnedMissingId = false;

/** The extension's Chrome Web Store ID, from the build environment. */
function extensionId(): string | undefined {
  const id = import.meta.env.VITE_LISTING_EXTENSION_ID as string | undefined;
  return id || undefined;
}

/** Where "Add to Chrome" points; undefined hides the link. */
export function extensionStoreUrl(): string | undefined {
  const url = import.meta.env.VITE_LISTING_EXTENSION_STORE_URL as string | undefined;
  return url || undefined;
}

function runtime(): ExtensionRuntime | undefined {
  return (globalThis as { chrome?: { runtime?: ExtensionRuntime } }).chrome?.runtime;
}

function isTouchDevice(uaData: UserAgentData | undefined): boolean {
  if (uaData?.mobile === true || uaData?.platform === 'Android') return true;
  const ua = navigator.userAgent;
  if (/iPhone|iPad|iPod|Android/i.test(ua)) return true;
  // iPadOS Safari reports a Mac user agent; only touch support tells it apart.
  return /Macintosh/.test(ua) && navigator.maxTouchPoints > 1;
}

function isDesktopChromium(uaData: UserAgentData | undefined): boolean {
  if (!uaData || uaData.mobile !== false) return false;
  return (uaData.brands ?? []).some((b) => b.brand === 'Chromium' || b.brand === 'Google Chrome');
}

function ping(id: string): Promise<boolean> {
  const send = runtime()?.sendMessage;
  if (!send) return Promise.resolve(false);
  return new Promise((resolve) => {
    const timer = setTimeout(() => resolve(false), PING_TIMEOUT_MS);
    try {
      send(id, { type: 'ping' }, (response) => {
        clearTimeout(timer);
        void runtime()?.lastError;
        resolve((response as { installed?: boolean } | undefined)?.installed === true);
      });
    } catch {
      clearTimeout(timer);
      resolve(false);
    }
  });
}

export async function detectPostCapability(): Promise<PostCapability> {
  const uaData = (navigator as Navigator & { userAgentData?: UserAgentData }).userAgentData;
  if (isTouchDevice(uaData)) return 'unsupported_touch';
  if (!isDesktopChromium(uaData)) return 'unsupported_desktop';

  const id = extensionId();
  if (!id) {
    if (!warnedMissingId) {
      warnedMissingId = true;
      console.warn('VITE_LISTING_EXTENSION_ID is not set; the listing extension cannot be detected.');
    }
    return 'installable';
  }
  return (await ping(id)) ? 'installed' : 'installable';
}

/**
 * Open the site's create page in a new tab, then tell the extension which listing it is for.
 * The tab is opened synchronously so the click's user activation survives the popup blocker.
 */
export async function startPost(listingId: string, site: ListingSiteDto): Promise<void> {
  window.open(SITE_CREATE_URL[site], '_blank', 'noopener,noreferrer');
  const id = extensionId();
  const send = runtime()?.sendMessage;
  if (!id || !send) throw new Error('The Resida extension is not available in this browser.');
  const response = await new Promise<unknown>((resolve) => {
    try {
      send(id, { type: 'handoff', listingId, site }, (r) => {
        void runtime()?.lastError;
        resolve(r);
      });
    } catch {
      resolve(undefined);
    }
  });
  if ((response as { ok?: boolean } | undefined)?.ok !== true) {
    throw new Error('The extension did not accept the listing. Open the extension and pick the listing there.');
  }
}
